import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/feature/Sidebar';
import OfflineIndicator from '../../components/base/OfflineIndicator';
import { Toast } from '../../components/base/Toast';
import { api } from '../../services/api';
import { FloatingActionButton } from '../../components/base/FloatingActionButton';
import { formatPrice } from '../../utils/format';
import ModeleFormModal from './components/ModeleFormModal';
import CategoriesManagerModal from './components/CategoriesManagerModal';

interface UserInfo { id: string; nom: string; role: string; }

interface Modele {
  id: string;
  nom: string;
  categorie: string;
  prix: number;
  image: string;
  description?: string;
}

interface Categorie {
  id: string;
  nom: string;
  couleur: string;
}

const DEFAULT_CATEGORIES: Categorie[] = [
  { id: 'cat_trad', nom: 'Traditionnel', couleur: 'bg-terre-100 text-terre-700' },
  { id: 'cat_mod', nom: 'Moderne', couleur: 'bg-foret-100 text-foret-700' },
  { id: 'cat_cer', nom: 'Cérémonie', couleur: 'bg-or-100 text-or-700' },
  { id: 'cat_cas', nom: 'Casual', couleur: 'bg-teal-100 text-teal-700' },
  { id: 'cat_hom', nom: 'Homme', couleur: 'bg-orange-100 text-orange-700' },
];

export default function CatalogueModeles() {
  const navigate = useNavigate();
  const [user, setUser] = useState<UserInfo | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [modeles, setModeles] = useState<Modele[]>([]);
  const [categories, setCategories] = useState<Categorie[]>(DEFAULT_CATEGORIES);
  const [activeCateg, setActiveCateg] = useState('Tous');
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [showCategoriesModal, setShowCategoriesModal] = useState(false);
  const [editingModele, setEditingModele] = useState<Modele | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const stored = localStorage.getItem('atelier_bassoum_user');
    const token = localStorage.getItem('atelier_bassoum_token');
    if (stored) setUser(JSON.parse(stored));
    else navigate('/login');

    if (token) {
        api.getModeles(token).then(data => {
            setModeles(data.map((m: any) => ({
                id: String(m.id),
                nom: m.nom,
                categorie: m.categorie || 'Moderne',
                prix: m.prix,
                image: m.image_url,
                description: m.description
            })));
            setLoading(false);
        }).catch(() => {
            showToast("Erreur chargement modèles", "error");
            setLoading(false);
        });
    }
  }, [navigate]);

  const filtered = modeles.filter(m => {
    const matchCateg = activeCateg === 'Tous' || m.categorie === activeCateg;
    const q = search.toLowerCase();
    const matchSearch = !q || m.nom.toLowerCase().includes(q) || m.categorie.toLowerCase().includes(q);
    return matchCateg && matchSearch;
  });

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleSave = async (data: Partial<Modele>, isEdit: boolean) => {
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;

    try {
        if (isEdit && editingModele) {
            await api.updateModele(token, editingModele.id, { ...data, image_url: data.image });
            setModeles(prev => prev.map(m => m.id === editingModele.id ? { ...m, ...data } as Modele : m));
            showToast(`"${data.nom}" mis à jour`);
        } else {
            // New Modele
            const res = await api.createModele(token, {
                nom: data.nom,
                categorie: data.categorie,
                prix: data.prix,
                image_url: data.image,
                description: data.description
            });
            // res contient maintenant l'objet complet (id, nom, categorie, prix, image_url)
            setModeles(prev => [...prev, { 
                ...res, 
                id: String(res.id), 
                image: res.image_url,
                categorie: res.categorie || data.categorie || 'Traditionnel'
            }]);
            showToast(`"${data.nom}" ajouté`);
        }
    } catch (err) {
        showToast("Erreur lors de l'enregistrement", "error");
    } finally {
        setShowForm(false);
        setEditingModele(null);
    }
  };

  const handleCategoriesChange = (newCats: Categorie[]) => {
    setCategories(newCats);
    if (activeCateg !== 'Tous' && !newCats.find(c => c.nom === activeCateg)) {
      setActiveCateg('Tous');
    }
    showToast(`Catégories mises à jour`);
  };

  const handleDelete = async (id: string) => {
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;
    try {
        // await api.deleteModele(token, id);
        setModeles(prev => prev.filter(m => m.id !== id));
        setConfirmDeleteId(null);
        showToast(`Modèle supprimé`);
    } catch (err) {
        showToast("Erreur suppression", "error");
    }
  };

  const openEdit = (m: Modele) => {
    setEditingModele(m);
    setShowForm(true);
  };

  const openAdd = () => {
    setEditingModele(null);
    setShowForm(true);
  };

  if (!user) return null;

  return (
    <div className="flex h-screen bg-gray-50 font-nunito overflow-hidden">
      <OfflineIndicator />
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      <div className="hidden md:block">
        <Sidebar role={user.role} nom={user.nom} collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(v => !v)} />
      </div>

      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        <header className="bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="font-playfair font-bold text-xl text-gray-900">Catalogue des modèles</h2>
            <p className="text-xs text-gray-400 mt-0.5">
              {modeles.length} modèles · {categories.length} catégories
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
               onClick={() => setShowCategoriesModal(true)}
               className="flex items-center gap-1.5 bg-white border border-gray-200 hover:bg-gray-50 text-gray-600 px-3 py-2 rounded-xl text-xs font-bold cursor-pointer transition-colors"
               title="Gérer les catégories"
            >
              <i className="ri-price-tag-3-line" /> 
              <span className="hidden sm:inline whitespace-nowrap">Catégories</span>
            </button>
            <button
              onClick={openAdd}
              className="hidden md:flex items-center gap-2 bg-foret-500 hover:bg-foret-600 text-white px-4 py-2 rounded-xl text-sm font-semibold cursor-pointer transition-colors whitespace-nowrap"
            >
              <i className="ri-add-line" /> Nouveau modèle
            </button>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-6">
          <div className="flex flex-col sm:flex-row gap-3 mb-6">
            <div className="relative flex-1 max-w-xs">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Rechercher un modèle…"
                className="w-full pl-9 pr-4 py-2 text-sm bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-foret-400"
              />
            </div>
            <div className="flex gap-1 bg-white border border-gray-200 rounded-xl p-1 overflow-x-auto">
              <button
                onClick={() => setActiveCateg('Tous')}
                className={`whitespace-nowrap px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer ${activeCateg === 'Tous' ? 'bg-foret-500 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
              >
                Tous ({modeles.length})
              </button>
              {categories.map(c => (
                <button
                  key={c.id}
                  onClick={() => setActiveCateg(c.nom)}
                  className={`whitespace-nowrap px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer ${activeCateg === c.nom ? 'bg-foret-500 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                >
                  {c.nom} ({modeles.filter(m => m.categorie === c.nom).length})
                </button>
              ))}
            </div>
          </div>

          {loading ? (
             <div className="flex items-center justify-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-foret-500"></div>
             </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16">
              <i className="ri-layout-masonry-line text-4xl text-gray-300 mb-3 block" />
              <p className="text-gray-500">Aucun modèle trouvé</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {filtered.map(m => {
                const cat = categories.find(c => c.nom === m.categorie);
                return (
                  <div key={m.id} className="bg-white rounded-xl border border-gray-100 overflow-hidden group hover:border-foret-300 transition-all">
                    <div className="relative h-44 overflow-hidden">
                      <img 
                        src={api.getProxyUrl(m.image) || 'https://via.placeholder.com/300?text=No+Image'} 
                        alt={m.nom} 
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300" 
                      />
                      <div className="absolute top-2 right-2 flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => openEdit(m)} title="Modifier" className="w-7 h-7 bg-white rounded-lg flex items-center justify-center cursor-pointer hover:bg-foret-500 hover:text-white transition-colors text-gray-700">
                          <i className="ri-pencil-line text-xs" />
                        </button>
                        <button onClick={() => setConfirmDeleteId(m.id)} title="Supprimer" className="w-7 h-7 bg-white rounded-lg flex items-center justify-center cursor-pointer hover:bg-red-500 hover:text-white transition-colors text-gray-700">
                          <i className="ri-delete-bin-line text-xs" />
                        </button>
                      </div>
                      <div className="absolute bottom-2 left-2">
                         <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${cat?.couleur ?? 'bg-white/90 text-gray-700'}`}>
                           {m.categorie}
                         </span>
                      </div>
                    </div>
                    <div className="p-3">
                      <p className="text-sm font-semibold text-gray-800 truncate">{m.nom}</p>
                      <p className="text-sm font-bold text-foret-600 mt-1">{formatPrice(m.prix)}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </main>
      </div>

      {showForm && (
        <ModeleFormModal
          modele={editingModele}
          onClose={() => { setShowForm(false); setEditingModele(null); }}
          onSave={handleSave}
        />
      )}

      {showCategoriesModal && (
        <CategoriesManagerModal
          categories={categories}
          onClose={() => setShowCategoriesModal(false)}
          onChange={handleCategoriesChange}
        />
      )}

      {confirmDeleteId && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full text-center">
            <h3 className="font-playfair font-bold text-lg mb-4">Confirmer la suppression ?</h3>
            <div className="flex gap-3">
              <button onClick={() => setConfirmDeleteId(null)} className="flex-1 py-2 rounded-xl bg-gray-100 font-semibold">Annuler</button>
              <button onClick={() => handleDelete(confirmDeleteId)} className="flex-1 py-2 rounded-xl bg-red-500 text-white font-semibold">Supprimer</button>
            </div>
          </div>
        </div>
      )}

      <FloatingActionButton 
        onClick={openAdd} 
        icon="ri-add-line" 
        label="Nouveau modèle" 
      />
    </div>
  );
}
